/**
 * Geometry for the Job Locations map.
 *
 * Everything here is expressed in WGS84 lon/lat and projected at render time
 * through {@link projectToFrame}. That matters because the map has two layers
 * that must line up:
 *
 *   1. the Amazon Location static basemap PNG (GET /api/admin/jobs-map-base),
 *      rendered for exactly {@link MAP_BOUNDS}; and
 *   2. this SVG overlay — job pins, state clusters, and the schematic coastline
 *      we fall back to when the basemap is unavailable.
 *
 * Both are drawn into the same {@link MAP_FRAME} viewBox — the basemap as an
 * `<image>` with `preserveAspectRatio="none"` — so the frame's stretch applies
 * identically to both and pins stay on their true position. The backend
 * renderer requests a PNG whose pixel aspect matches the bounds' Mercator
 * aspect, so Amazon returns the requested bounds rather than padding them out;
 * keep `map-renderer.ts` in sync if either changes.
 */

/** Longitude/latitude bounds of the rendered frame. */
export const MAP_BOUNDS = { west: 112, east: 154, north: -10, south: -44 } as const;

/**
 * SVG user-space size of the frame, and therefore the shape the continent is
 * drawn in. The bounds' true Mercator aspect is ≈1.076; the frame widens that
 * to 1.30 so the landmass fills a wide dashboard panel instead of sitting in a
 * narrow column of ocean. That is a deliberate, uniform horizontal stretch of
 * the projection — the basemap image is stretched into the same frame, so pins
 * stay registered on it. The panel letterboxes the frame with
 * `preserveAspectRatio="xMidYMid meet"` rather than distorting it further.
 */
export const MAP_FRAME = { width: 1300, height: 1000 } as const;

/** Spherical Mercator northing (unitless), clamped to the valid range. */
function mercatorY(latitude: number): number {
  const clamped = Math.max(-85, Math.min(85, latitude));
  return Math.log(Math.tan(Math.PI / 4 + (clamped * Math.PI) / 360));
}

const NORTH_Y = mercatorY(MAP_BOUNDS.north);
const SOUTH_Y = mercatorY(MAP_BOUNDS.south);

/** Project lon/lat into the frame's user-space coordinates. */
export function projectToFrame(longitude: number, latitude: number): { x: number; y: number } {
  const x = ((longitude - MAP_BOUNDS.west) / (MAP_BOUNDS.east - MAP_BOUNDS.west)) * MAP_FRAME.width;
  const y = ((NORTH_Y - mercatorY(latitude)) / (NORTH_Y - SOUTH_Y)) * MAP_FRAME.height;
  return { x, y };
}

/** Build an SVG path `d` from a lon/lat ring or line. */
export function toPath(points: readonly (readonly [number, number])[], close: boolean): string {
  const d = points
    .map(([lon, lat], index) => {
      const { x, y } = projectToFrame(lon, lat);
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  return close ? `${d} Z` : d;
}

/**
 * Coarse mainland coastline, clockwise from the North West Cape. Schematic —
 * it exists so the fallback view reads as Australia at a glance, not for
 * measurement. Roughly 1 point per 150 km of coast.
 */
export const MAINLAND: readonly (readonly [number, number])[] = [
  [113.2, -22.3],
  [114.1, -21.8],
  [115.0, -21.0],
  [116.7, -20.6],
  [118.6, -20.3],
  [121.0, -19.6],
  [122.2, -18.1],
  [123.6, -17.5],
  [125.3, -16.4],
  [126.6, -14.0],
  [128.0, -15.4],
  [129.0, -15.5],
  [129.7, -14.9],
  [130.6, -12.4],
  [132.0, -12.2],
  [132.6, -11.4],
  [133.4, -11.7],
  [134.5, -12.0],
  [135.4, -12.2],
  [136.0, -12.0],
  [136.9, -12.4],
  [136.5, -13.5],
  [135.9, -14.5],
  [135.5, -15.0],
  [137.0, -15.9],
  [138.0, -16.6],
  [139.5, -17.3],
  [140.8, -17.5],
  [141.6, -15.0],
  [141.5, -13.5],
  [142.5, -10.7],
  [143.5, -14.0],
  [144.5, -14.5],
  [145.3, -15.0],
  [146.0, -17.0],
  [148.0, -20.0],
  [149.5, -22.0],
  [150.9, -23.5],
  [152.0, -25.0],
  [153.2, -26.0],
  [153.6, -28.6],
  [152.9, -31.0],
  [151.2, -33.9],
  [150.2, -35.7],
  [149.9, -37.5],
  [148.0, -37.8],
  [146.4, -38.9],
  [145.0, -38.4],
  [144.6, -38.3],
  [143.5, -38.8],
  [141.6, -38.4],
  [140.0, -37.5],
  [139.5, -36.0],
  [139.8, -35.6],
  // The Spencer/St Vincent gulfs are only a couple of pixels wide at dashboard
  // scale, so the south coast is traced as a single smooth line through them.
  [138.5, -35.4],
  [137.4, -35.2],
  [136.7, -35.0],
  [135.8, -34.6],
  [134.5, -33.2],
  [132.0, -32.0],
  [130.0, -31.6],
  [128.0, -31.7],
  [126.0, -32.3],
  [124.0, -33.0],
  [123.5, -34.0],
  [121.0, -33.9],
  [119.0, -34.5],
  [117.5, -35.1],
  [115.5, -34.4],
  [115.1, -33.6],
  [115.7, -32.5],
  [115.8, -31.9],
  [114.9, -30.3],
  [114.6, -28.8],
  [113.7, -26.1],
  [113.4, -24.5],
];

export const TASMANIA: readonly (readonly [number, number])[] = [
  [144.7, -40.7],
  [146.0, -41.0],
  [147.4, -40.8],
  [148.3, -40.9],
  [148.3, -42.1],
  [147.9, -43.0],
  [146.9, -43.6],
  [146.0, -43.5],
  [145.5, -42.5],
  [144.7, -41.2],
];

/** State borders, drawn as open polylines. */
export const BORDERS: readonly (readonly (readonly [number, number])[])[] = [
  // WA / NT / SA — the 129°E meridian.
  [
    [129, -13.9],
    [129, -31.7],
  ],
  // NT / SA and SA / QLD — the 26°S parallel.
  [
    [129, -26],
    [141, -26],
  ],
  // NT / QLD — the 138°E meridian.
  [
    [138, -16.6],
    [138, -26],
  ],
  // SA / NSW and SA / VIC — the 141°E meridian.
  [
    [141, -26],
    [141, -38.1],
  ],
  // QLD / NSW.
  [
    [141, -29],
    [148.9, -29],
    [152.5, -28.2],
  ],
  // NSW / VIC — the Murray, heavily simplified.
  [
    [141, -34],
    [143.5, -34.7],
    [146, -36],
    [147.5, -36],
    [148.2, -36.5],
    [149.9, -37.5],
  ],
];

/** Schematic state labels for the fallback view. */
export const STATE_LABELS: readonly { label: string; lon: number; lat: number }[] = [
  { label: 'WESTERN AUSTRALIA', lon: 121.5, lat: -25.5 },
  { label: 'NORTHERN TERRITORY', lon: 133.5, lat: -20.5 },
  { label: 'SOUTH AUSTRALIA', lon: 134.5, lat: -29.5 },
  { label: 'QUEENSLAND', lon: 144.5, lat: -22.5 },
  { label: 'NEW SOUTH WALES', lon: 146.5, lat: -32.3 },
  { label: 'VICTORIA', lon: 144.0, lat: -36.9 },
  { label: 'TASMANIA', lon: 143.4, lat: -42.2 },
];

/** Capital city labelled under each state's cluster badge. */
export const STATE_CITY: Readonly<Record<string, string>> = {
  WA: 'Perth',
  NT: 'Darwin',
  SA: 'Adelaide',
  QLD: 'Brisbane',
  NSW: 'Sydney',
  ACT: 'Canberra',
  VIC: 'Melbourne',
  TAS: 'Hobart',
};
